import { useState } from 'react'
import { ExternalLink, LogOut, Shield } from 'lucide-react'
import Markdown from 'react-markdown'

import { Button } from '@/components/ui/button'
import { Checkbox } from '@/components/ui/checkbox'
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog'
import { ScrollArea } from '@/components/ui/scroll-area'
import { Separator } from '@/components/ui/separator'
import { ButtonSpinner } from '@/components/ui/loading-spinner'
import { useAcceptLegal, useLegalDocument } from '@/hooks/use-legal-acceptance'
import { useAuth } from '@/hooks/use-auth'
import type { LegalDocumentType, PendingDocument } from '@/types/legal'

interface LegalBlockingModalProps {
  open: boolean
  documents: PendingDocument[]
  onAccepted: () => void
}

function getDocumentRoute(type: LegalDocumentType): string {
  const routes: Record<LegalDocumentType, string> = {
    terms_of_use: '/terms-of-use',
    privacy_policy: '/privacy-policy',
  }

  return routes[type]
}

export function LegalBlockingModal({ open, documents, onAccepted }: LegalBlockingModalProps) {
  const [acceptedDocuments, setAcceptedDocuments] = useState<Set<string>>(new Set())
  const [currentDocIndex, setCurrentDocIndex] = useState(0)
  const [isChecked, setIsChecked] = useState(false)
  const { logout, isLoggingOut } = useAuth()

  const currentDoc = documents[currentDocIndex]

  const { data: fullDocument, isLoading: isLoadingDocument } = useLegalDocument(
    currentDoc?.documentType ?? 'terms_of_use',
    !!currentDoc,
  )

  const acceptMutation = useAcceptLegal({
    onSuccess: () => {
      if (!currentDoc) return

      const newAccepted = new Set(acceptedDocuments)

      newAccepted.add(currentDoc.versionId)
      setAcceptedDocuments(newAccepted)
      setIsChecked(false)

      if (currentDocIndex < documents.length - 1) {
        setCurrentDocIndex(currentDocIndex + 1)
      } else {
        onAccepted()
      }
    },
  })

  const handleAccept = () => {
    if (!currentDoc) return

    acceptMutation.mutate({
      documentVersionId: currentDoc.versionId,
      acceptanceMethod: 'modal_checkbox',
    })
  }

  const handleLogout = () => {
    logout()
  }

  const allDocsAccepted = acceptedDocuments.size === documents.length

  if (!currentDoc) return null

  const documentRoute = getDocumentRoute(currentDoc.documentType)

  return (
    <Dialog open={open && !allDocsAccepted}>
      <DialogContent
        showCloseButton={false}
        className='sm:max-w-2xl max-h-[90vh] flex flex-col'
        onInteractOutside={(e) => e.preventDefault()}
        onEscapeKeyDown={(e) => e.preventDefault()}
        data-testid='legal-blocking-modal'
      >
        <DialogHeader className='shrink-0'>
          <div className='flex items-center gap-2 text-primary'>
            <Shield className='h-6 w-6' />
            <span className='text-xl font-semibold'>BxR CRM</span>
          </div>
          <DialogTitle className='text-xl mt-4'>
            Atualização {documents.length > 1 ? 'nos Termos' : `no ${currentDoc.documentName}`}
          </DialogTitle>
          <DialogDescription>
            Para continuar usando o sistema, você precisa revisar e aceitar{' '}
            {documents.length > 1 ? 'os novos termos' : 'o novo documento'}.
            {documents.length > 1 && (
              <span className='block mt-1 text-xs'>
                Documento {currentDocIndex + 1} de {documents.length}
              </span>
            )}
          </DialogDescription>
        </DialogHeader>

        <div className='flex-1 min-h-0 flex flex-col gap-4 overflow-hidden'>
          {currentDoc.summary && (
            <div className='bg-muted/50 rounded-lg p-4 shrink-0'>
              <h4 className='font-medium text-sm mb-2'>O que mudou:</h4>
              <div className='text-sm text-muted-foreground prose prose-sm dark:prose-invert max-w-none'>
                <Markdown>{currentDoc.summary}</Markdown>
              </div>
            </div>
          )}

          <div className='flex-1 min-h-0 border rounded-lg overflow-hidden'>
            <div className='bg-muted/30 px-4 py-2 border-b flex items-center justify-between'>
              <span className='text-sm font-medium'>{currentDoc.title}</span>
              <Button
                variant='ghost'
                size='sm'
                className='text-xs'
                onClick={() => window.open(documentRoute, '_blank')}
              >
                Ver completo
                <ExternalLink className='h-3 w-3 ml-1' />
              </Button>
            </div>
            <ScrollArea className='h-48'>
              <div className='p-4 text-sm text-muted-foreground'>
                {isLoadingDocument ? (
                  <div className='flex items-center justify-center py-8'>
                    <ButtonSpinner label='Carregando documento...' />
                  </div>
                ) : fullDocument?.currentVersion?.content ? (
                  <div className='prose prose-sm dark:prose-invert max-w-none'>
                    <Markdown>{fullDocument.currentVersion.content}</Markdown>
                  </div>
                ) : (
                  <p className='text-center text-muted-foreground py-8'>
                    Não foi possível carregar o documento.
                  </p>
                )}
              </div>
            </ScrollArea>
          </div>
        </div>

        <Separator />

        <div className='shrink-0 space-y-4'>
          <div className='flex items-start gap-3'>
            <Checkbox
              id='legal-blocking-modal-checkbox'
              checked={isChecked}
              onCheckedChange={(checked) => setIsChecked(checked === true)}
              data-testid='legal-blocking-modal-checkbox'
            />
            <label
              htmlFor='legal-blocking-modal-checkbox'
              className='text-sm cursor-pointer select-none leading-relaxed'
            >
              Li e concordo com {currentDoc.documentName}
            </label>
          </div>

          <DialogFooter className='flex-col sm:flex-row gap-2'>
            <Button
              onClick={handleAccept}
              disabled={!isChecked || acceptMutation.isPending}
              className='w-full sm:w-auto'
              data-testid='legal-blocking-modal-accept-button'
            >
              {acceptMutation.isPending ? (
                <ButtonSpinner label='Processando...' />
              ) : (
                'Aceitar e continuar'
              )}
            </Button>
          </DialogFooter>

          <Separator />

          <div className='text-center'>
            <Button
              variant='ghost'
              size='sm'
              onClick={handleLogout}
              disabled={isLoggingOut}
              className='text-muted-foreground hover:text-foreground'
              data-testid='legal-blocking-modal-logout-button'
            >
              <LogOut className='h-4 w-4 mr-2' />
              {isLoggingOut ? 'Saindo...' : 'Sair do sistema'}
            </Button>
          </div>
        </div>
      </DialogContent>
    </Dialog>
  )
}
